/**
 * Succession Plans — Phase 3 (Admin only)
 *
 * Vue : liste des postes critiques avec plan de succession.
 * Pour chaque plan : titulaire + successeurs (auto-suggérés + validés manuellement).
 * AI : bouton "Analyse risque IA" sur chaque plan (Admin RH).
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Crown, Plus, Pencil, Trash2, AlertTriangle, ShieldAlert, ShieldCheck,
  Users, Sparkles, UserPlus, Check, X, Star,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import AIInsightModal from '../components/career/AIInsightModal';

const riskColor = (r) => ({
  low:      'bg-emerald-100 text-emerald-700 border-emerald-200',
  medium:   'bg-amber-100 text-amber-700 border-amber-200',
  high:     'bg-orange-100 text-orange-700 border-orange-200',
  critical: 'bg-red-100 text-red-700 border-red-200',
}[r] || 'bg-slate-100 text-slate-700');

const riskLabel = (r) => ({ low: 'Faible', medium: 'Moyen', high: 'Élevé', critical: 'Critique' }[r] || r);
const statusLabel = (s) => ({ suggested: 'Suggéré', validated: 'Validé', rejected: 'Écarté', ready: 'Prêt' }[s] || s);

// ─────────────────────────────────────────────
// Plan Form Dialog (create/edit)
// ─────────────────────────────────────────────
function PlanFormDialog({ open, onOpenChange, plan, profiles, employees, onSaved }) {
  const empty = { positionId: '', incumbentEmployeeId: '', riskLevel: 'medium', horizonMonths: 12, notes: '' };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm(plan ? { ...empty, ...plan } : empty);
  }, [plan, open]);

  const save = async () => {
    if (!plan && !form.positionId) {
      toast.error('Choisissez un poste à sécuriser');
      return;
    }
    try {
      setLoading(true);
      if (plan?.id) {
        await api.put(`/career-intel/succession-plans/${plan.id}`, {
          incumbentEmployeeId: form.incumbentEmployeeId || null,
          riskLevel: form.riskLevel,
          horizonMonths: parseInt(form.horizonMonths, 10) || 12,
          notes: form.notes,
        });
        toast.success('Plan mis à jour');
      } else {
        await api.post('/career-intel/succession-plans', {
          positionId: form.positionId,
          incumbentEmployeeId: form.incumbentEmployeeId || null,
          riskLevel: form.riskLevel,
          horizonMonths: parseInt(form.horizonMonths, 10) || 12,
          notes: form.notes,
        });
        toast.success('Plan créé');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="plan-form-dialog">
        <DialogHeader>
          <DialogTitle>{plan?.id ? 'Modifier le plan' : 'Nouveau plan de succession'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {!plan?.id && (
            <div>
              <Label>Poste à sécuriser *</Label>
              <Select value={form.positionId} onValueChange={(v) => setForm({ ...form, positionId: v })}>
                <SelectTrigger data-testid="plan-position-select"><SelectValue placeholder="Choisir une fiche de poste" /></SelectTrigger>
                <SelectContent>
                  {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Titulaire actuel</Label>
            <Select value={form.incumbentEmployeeId || 'none'} onValueChange={(v) => setForm({ ...form, incumbentEmployeeId: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="plan-incumbent-select"><SelectValue placeholder="(facultatif)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Non spécifié —</SelectItem>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.firstName} {e.lastName} — {e.jobTitle || '—'}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Niveau de risque</Label>
              <Select value={form.riskLevel} onValueChange={(v) => setForm({ ...form, riskLevel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Faible</SelectItem>
                  <SelectItem value="medium">Moyen</SelectItem>
                  <SelectItem value="high">Élevé</SelectItem>
                  <SelectItem value="critical">Critique</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Horizon (mois)</Label>
              <Input type="number" min={1} value={form.horizonMonths}
                onChange={(e) => setForm({ ...form, horizonMonths: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Contexte, contraintes spécifiques..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={loading} data-testid="plan-save-btn">
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// Auto-suggest Dialog
// ─────────────────────────────────────────────
function AutoSuggestDialog({ open, onOpenChange, plan, onAdded }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !plan) return;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/career-intel/succession-plans/auto-suggest/${plan.positionId}?limit=10`);
        setCandidates(res.data?.candidates || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Erreur');
      } finally { setLoading(false); }
    })();
  }, [open, plan]);

  const addAsSuccessor = async (cand, status) => {
    try {
      await api.post(`/career-intel/succession-plans/${plan.id}/successors`, {
        employeeId: cand.id, status,
      });
      toast.success(status === 'suggested' ? 'Successeur ajouté (suggéré)' : 'Successeur validé');
      setCandidates(candidates.filter(c => c.id !== cand.id));
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="auto-suggest-dialog">
        <DialogHeader>
          <DialogTitle>Successeurs auto-suggérés</DialogTitle>
          <DialogDescription>
            Classement par readiness pour le poste : <strong>{plan?.position?.name}</strong>
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-8">Calcul...</p>
        ) : candidates.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500">
            Aucun candidat évalué dans le périmètre actuel.
          </div>
        ) : (
          <div className="space-y-2">
            {candidates.map(c => (
              <div key={c.id} className="p-3 border rounded-lg flex items-center justify-between gap-3 hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{c.firstName} {c.lastName}</p>
                  <p className="text-xs text-slate-500">{c.jobTitle || '—'}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">Readiness {c.readiness}%</Badge>
                    {c.criticalBlocking && (
                      <Badge className="text-xs bg-red-100 text-red-700">
                        <AlertTriangle className="w-3 h-3 mr-1" />Bloquant
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => addAsSuccessor(c, 'suggested')}
                    data-testid={`suggest-${c.id}`}>
                    + Suggéré
                  </Button>
                  <Button size="sm" onClick={() => addAsSuccessor(c, 'validated')}
                    data-testid={`validate-${c.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" />Valider
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// Plan Card
// ─────────────────────────────────────────────
function PlanCard({ plan, onEdit, onDelete, onAutoSuggest, onAI, onRemoveSuccessor, onChangeSuccessorStatus }) {
  const validated = (plan.successors || []).filter(s => s.status === 'validated' || s.status === 'ready');
  const suggested = (plan.successors || []).filter(s => s.status === 'suggested');

  return (
    <Card data-testid={`plan-card-${plan.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge variant="outline" className={riskColor(plan.riskLevel)}>
                {plan.riskLevel === 'critical' || plan.riskLevel === 'high' ? <ShieldAlert className="w-3 h-3 mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                Risque {riskLabel(plan.riskLevel)}
              </Badge>
              <Badge variant="outline" className="text-xs">{plan.horizonMonths || 12} mois</Badge>
              <Badge variant="outline" className="text-xs">
                <Users className="w-3 h-3 mr-1" />{validated.length} validé(s) · {suggested.length} suggéré(s)
              </Badge>
            </div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-500" />
              {plan.position?.name || '— poste supprimé —'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Titulaire : {plan.incumbent ? `${plan.incumbent.firstName} ${plan.incumbent.lastName}` : <em>non spécifié</em>}
            </p>
            {plan.notes && <p className="text-xs text-slate-600 mt-1 italic">"{plan.notes}"</p>}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => onAI(plan)} data-testid={`ai-plan-${plan.id}`}>
              <Sparkles className="w-4 h-4 text-indigo-600" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onEdit(plan)} data-testid={`edit-plan-${plan.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(plan)} data-testid={`delete-plan-${plan.id}`}>
              <Trash2 className="w-4 h-4 text-red-600" />
            </Button>
          </div>
        </div>

        {(plan.successors || []).length > 0 && (
          <div className="space-y-1.5 mb-3">
            {(plan.successors || []).map(s => (
              <div key={s.id} className="flex items-center justify-between p-2 rounded bg-slate-50 gap-2" data-testid={`successor-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {s.employee ? `${s.employee.firstName} ${s.employee.lastName}` : '— employé inconnu —'}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">{statusLabel(s.status)}</Badge>
                    {s.snapshot?.readiness != null && (
                      <span className="text-xs text-slate-500">Readiness {s.snapshot.readiness}%</span>
                    )}
                    {s.snapshot?.criticalBlocking && (
                      <span className="text-xs text-red-600">⚠ Critiques bloquantes</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  {s.status !== 'validated' && (
                    <Button size="sm" variant="ghost" onClick={() => onChangeSuccessorStatus(plan.id, s.id, 'validated')}>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onRemoveSuccessor(plan.id, s.id)}>
                    <X className="w-3.5 h-3.5 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button size="sm" variant="outline" className="w-full" onClick={() => onAutoSuggest(plan)}
          data-testid={`auto-suggest-btn-${plan.id}`}>
          <UserPlus className="w-3.5 h-3.5 mr-2" />Ajouter un successeur (auto-suggestion)
        </Button>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
export default function SuccessionPlansPage() {
  const [plans, setPlans] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [autoSuggestPlan, setAutoSuggestPlan] = useState(null);
  const [aiTarget, setAiTarget] = useState(null);
  const [aiCosts, setAiCosts] = useState({ costs: {}, balance: 0 });

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [plansRes, profRes, empRes, costsRes] = await Promise.all([
        api.get('/career-intel/succession-plans'),
        api.get('/career-intel/job-profiles/lite'),
        api.get('/employees?limit=2000'),
        api.get('/career-intel/ai/costs').catch(() => ({ data: { costs: {}, balance: 0 } })),
      ]);
      setPlans(plansRes.data?.items || []);
      setProfiles(profRes.data?.items || []);
      const emps = (empRes.data?.employees || empRes.data || [])
        .filter(e => (e.status || 'Actif') === 'Actif')
        .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'fr'));
      setEmployees(emps);
      setAiCosts(costsRes.data || { costs: {}, balance: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (plan) => {
    if (!window.confirm(`Supprimer le plan pour "${plan.position?.name}" ?`)) return;
    try {
      await api.delete(`/career-intel/succession-plans/${plan.id}`);
      toast.success('Plan supprimé');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const handleChangeSuccessorStatus = async (planId, successorId, status) => {
    try {
      await api.put(`/career-intel/succession-plans/${planId}/successors/${successorId}`, { status });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const handleRemoveSuccessor = async (planId, successorId) => {
    if (!window.confirm('Retirer ce successeur du plan ?')) return;
    try {
      await api.delete(`/career-intel/succession-plans/${planId}/successors/${successorId}`);
      toast.success('Successeur retiré');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  // Postes encore disponibles pour créer un plan
  const usedPositionIds = new Set(plans.map(p => p.positionId));
  const availableProfiles = profiles.filter(p => !usedPositionIds.has(p.id));

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="succession-plans-page">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" />
              Plans de Succession
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Identifiez les postes critiques et sécurisez la relève avec des successeurs auto-suggérés + validés manuellement.
            </p>
          </div>
          <Button onClick={() => { setEditTarget(null); setFormOpen(true); }}
            disabled={availableProfiles.length === 0}
            data-testid="new-plan-btn">
            <Plus className="w-4 h-4 mr-2" />Nouveau plan
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 text-center py-8">Chargement...</p>
        ) : plans.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500">
            <Crown className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            Aucun plan de succession. Créez votre premier plan en partant d'un poste critique.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {plans.map(plan => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onEdit={(p) => { setEditTarget(p); setFormOpen(true); }}
                onDelete={handleDelete}
                onAutoSuggest={(p) => setAutoSuggestPlan(p)}
                onAI={(p) => setAiTarget(p)}
                onChangeSuccessorStatus={handleChangeSuccessorStatus}
                onRemoveSuccessor={handleRemoveSuccessor}
              />
            ))}
          </div>
        )}

        <PlanFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          plan={editTarget}
          profiles={editTarget ? profiles : availableProfiles}
          employees={employees}
          onSaved={fetchAll}
        />

        <AutoSuggestDialog
          open={!!autoSuggestPlan}
          onOpenChange={(v) => { if (!v) setAutoSuggestPlan(null); }}
          plan={autoSuggestPlan}
          onAdded={fetchAll}
        />

        {aiTarget && (
          <AIInsightModal
            open={!!aiTarget}
            onOpenChange={(v) => { if (!v) setAiTarget(null); }}
            title={`Analyse risque succession — ${aiTarget.position?.name || ''}`}
            description="Synthèse IA pour le Comité Talents : diagnostic, forces, faiblesses, actions à 90 jours."
            endpoint={`/career-intel/ai/succession-risk/${aiTarget.positionId}`}
            costInfo={{
              cost: aiCosts.costs?.career_succession_risk?.cost,
              balance: aiCosts.balance,
            }}
            resultField="analysis"
          />
        )}
      </div>
    </DashboardLayout>
  );
}
